import type { ResponseType } from 'ofetch'
import { useNuxtApp, useSanctumClient } from '#imports'
import { AppProblemModel } from '@/services/api/AppProblemModel'
import { useStepUp } from '@/composables/useStepUp'
import { handleHttpError } from '@/composables/http/handleHttpError'
import type { ApiRequest } from '~/types/ApiRequest'
import type { ApiRequestOptions } from '~/types/ApiRequestOptions'

/**
 * Methods with no side effects, per RFC 9110, and so the only ones repeated.
 *
 * PUT and DELETE are idempotent in the state they leave behind, but a repeat
 * can still fire a side effect a second time -- review approve/reject notifies
 * -- or turn a delete whose response was lost into a 404 the user reads as a
 * failure. Neither is worth a retry the API cannot tell us is safe.
 */
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS'])

const MAX_RETRY_ATTEMPTS = 2
const MAX_RETRY_DELAY_MS = 10_000
const DEFAULT_RETRY_DELAY_MS = 1_000

const isSafe = (method: string | undefined): boolean =>
  SAFE_METHODS.has((method ?? 'GET').toUpperCase())

const retryDelayMs = (problem: AppProblemModel): number =>
  problem.retryDelaySeconds === null
    ? DEFAULT_RETRY_DELAY_MS
    : Math.min(problem.retryDelaySeconds * 1_000, MAX_RETRY_DELAY_MS)

const wait = (milliseconds: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, milliseconds))

/**
 * The application's API transport.
 *
 * Failures arrive here already normalized and already presented, because the
 * api-error-policy plugin owns both. This adds only retry, which the contract
 * drives: the API states whether a failure is worth repeating and how long to
 * wait, so no call site reimplements that decision.
 */
export const useApiRequest = (): ApiRequest => {
  const sanctumClient = useSanctumClient()
  const nuxtApp = useNuxtApp()

  return async <T = unknown, R extends ResponseType = 'json'>(
    url: string,
    options?: ApiRequestOptions<R, T>
  ): Promise<T> => {
    const { globalErrorUi = false, ...fetchOptions } = options ?? {}
    // globalErrorUi belongs to this layer, not the transport, so it is removed
    // before the request is made. A call whose only option was this flag is
    // forwarded with no options at all rather than an empty object, keeping
    // the arity every downstream transport observes unchanged.
    const transportOptions =
      Object.keys(fetchOptions).length === 0 ? undefined : (fetchOptions as typeof options)

    // A single step-up prompt per request: if the replayed call still demands
    // one, the confirmation did not take and we surface the failure instead of
    // looping on the modal.
    let steppedUp = false

    for (let attempt = 0; ; attempt += 1) {
      try {
        // Forwarded exactly as received: passing an explicit undefined would
        // change the arity every downstream transport observes.
        return (
          transportOptions === undefined
            ? await sanctumClient<T, R>(url)
            : await sanctumClient<T, R>(url, transportOptions)
        ) as T
      } catch (error: unknown) {
        const problem = AppProblemModel.normalize(error, { context: 'api request' })

        // Step-up: a financial-routing or permission-changing action needs a
        // fresh two-factor confirmation (API `mfa.confirm`). Prompt for a code,
        // then replay the original request once — regardless of method, since
        // the user just re-authorized it. Client-only: SSR cannot show a modal,
        // so a 423 there falls through and is thrown like any other failure.
        if (problem.code === 'STEP_UP_REQUIRED' && import.meta.client && !steppedUp) {
          steppedUp = true
          if (await useStepUp().requestStepUp()) continue
          // Dismissed: fall through to the standard failure handling below so
          // globalErrorUi and the throw stay consistent with every other code.
        }

        const worthRepeating =
          problem.retryable && attempt < MAX_RETRY_ATTEMPTS && isSafe(options?.method)

        if (worthRepeating) {
          await wait(retryDelayMs(problem))
          continue
        }

        // Reactions to a failure, such as refreshing access, live in plugins
        // bound to this hook; awaiting keeps their ordering with the error UI.
        await nuxtApp.callHook('mealz:api-problem', problem)

        // handleHttpError decides what a code means for the interface and
        // ignores anything it does not own. It is called on the server too: a
        // page whose subject is missing should render its error during SSR
        // rather than after hydration.
        if (globalErrorUi) {
          await handleHttpError(problem)
        }

        throw problem
      }
    }
  }
}
